import { useCallback } from 'react';
import { 
  logger, 
  trackUserAction, 
  trackPerformance, 
  trackApiCall,
  getSessionId 
} from '../index';

export interface UseStackScopeLoggerReturn {
  debug: (msg: string, meta?: Record<string, unknown>) => void;
  info: (msg: string, meta?: Record<string, unknown>) => void;
  warn: (msg: string, meta?: Record<string, unknown>) => void;
  error: (msg: string, meta?: Record<string, unknown>) => void;
  sessionId: string | null;
}

export function useStackScopeLogger(): UseStackScopeLoggerReturn {
  const debug = useCallback((msg: string, meta?: Record<string, unknown>) => {
    logger.debug(msg, { ...meta, component: 'React' });
  }, []);

  const info = useCallback((msg: string, meta?: Record<string, unknown>) => {
    logger.info(msg, { ...meta, component: 'React' });
  }, []);

  const warn = useCallback((msg: string, meta?: Record<string, unknown>) => {
    logger.warn(msg, { ...meta, component: 'React' });
  }, []);

  const error = useCallback((msg: string, meta?: Record<string, unknown>) => {
    logger.error(msg, { ...meta, component: 'React' });
  }, []);

  return {
    debug,
    info,
    warn,
    error,
    sessionId: getSessionId()
  };
}

export interface UsePerformanceTrackingReturn {
  trackMetric: (metric: string, value: number, unit?: string) => void;
  trackRenderTime: (componentName: string, renderTime: number) => void;
  trackLoadTime: (resourceName: string, loadTime: number) => void;
}

export function usePerformanceTracking(): UsePerformanceTrackingReturn {
  const trackMetric = useCallback((metric: string, value: number, unit?: string) => {
    trackPerformance(metric, value, unit);
  }, []);

  const trackRenderTime = useCallback((componentName: string, renderTime: number) => {
    trackPerformance(`${componentName} render time`, renderTime, 'ms');
  }, []);

  const trackLoadTime = useCallback((resourceName: string, loadTime: number) => {
    trackPerformance(`${resourceName} load time`, loadTime, 'ms');
  }, []);

  return {
    trackMetric,
    trackRenderTime,
    trackLoadTime
  };
}

export interface UseUserActionTrackingReturn {
  trackClick: (element: string, details?: Record<string, unknown>) => void;
  trackFormSubmit: (formName: string, details?: Record<string, unknown>) => void;
  trackPageView: (pageName: string, details?: Record<string, unknown>) => void;
  trackCustomAction: (action: string, details?: Record<string, unknown>) => void;
}

export function useUserActionTracking(): UseUserActionTrackingReturn {
  const trackClick = useCallback((element: string, details?: Record<string, unknown>) => {
    trackUserAction('click', { element, ...details });
  }, []);

  const trackFormSubmit = useCallback((formName: string, details?: Record<string, unknown>) => {
    trackUserAction('form_submit', { formName, ...details });
  }, []);

  const trackPageView = useCallback((pageName: string, details?: Record<string, unknown>) => {
    trackUserAction('page_view', { pageName, ...details });
  }, []);

  const trackCustomAction = useCallback((action: string, details?: Record<string, unknown>) => {
    trackUserAction(action, details);
  }, []);

  return {
    trackClick,
    trackFormSubmit,
    trackPageView,
    trackCustomAction
  };
}

export interface UseApiLoggingReturn {
  logApiCall: (url: string, method: string, status: number, duration: number, meta?: Record<string, unknown>) => void;
  logApiSuccess: (url: string, method: string, duration: number, meta?: Record<string, unknown>) => void;
  logApiError: (url: string, method: string, status: number, duration: number, error?: Error, meta?: Record<string, unknown>) => void;
}

export function useApiLogging(): UseApiLoggingReturn {
  const logApiCall = useCallback((url: string, method: string, status: number, duration: number, meta?: Record<string, unknown>) => {
    trackApiCall(url, method, status, duration, meta);
  }, []);

  const logApiSuccess = useCallback((url: string, method: string, duration: number, meta?: Record<string, unknown>) => {
    trackApiCall(url, method, 200, duration, meta);
  }, []);

  const logApiError = useCallback((url: string, method: string, status: number, duration: number, error?: Error, meta?: Record<string, unknown>) => {
    trackApiCall(url, method, status, duration, {
      ...meta,
      error: error?.message,
      stack: error?.stack
    });
  }, []);

  return {
    logApiCall,
    logApiSuccess,
    logApiError
  };
}

export interface UseErrorReportingReturn {
  reportError: (error: Error, context?: string, meta?: Record<string, unknown>) => void;
  reportAsyncError: (error: Error, operation?: string, meta?: Record<string, unknown>) => void;
}

export function useErrorReporting(): UseErrorReportingReturn {
  const reportError = useCallback((error: Error, context?: string, meta?: Record<string, unknown>) => {
    logger.error(`Error ${context ? `in ${context}` : ''}`, {
      ...meta,
      error: error.message,
      stack: error.stack,
      component: 'React'
    });
  }, []);

  const reportAsyncError = useCallback((error: Error, operation?: string, meta?: Record<string, unknown>) => {
    logger.error(`Async error ${operation ? `during ${operation}` : ''}`, {
      ...meta,
      error: error.message,
      stack: error.stack,
      component: 'React',
      type: 'async-error'
    });
  }, []);

  return {
    reportError,
    reportAsyncError
  };
}